import React from "react";
import {
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { dateConvertor } from "../../utils/dateConvertor";
import {
  EdgeDeviceProvisionCommandOptions,
  EdgeDeviceProvisionTokenResponse,
} from "./edgeDeviceInterfaces";
import {
  buildBashProvisionCommand,
  buildPowershellProvisionCommand,
} from "./provisionCommands";
import {
  downloadEdgeDeviceBootstrapZip,
  parseProvisionTokenError,
  requestEdgeDeviceProvisionToken,
} from "./fetchEdgeDevices";

const fallbackCopyToClipboard = async (value: string): Promise<void> => {
  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

const copyTextToClipboard = async (value: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
  } else {
    await fallbackCopyToClipboard(value);
  }
};

interface CommandBlockProps {
  title: string;
  command: string;
  copyLabel: string;
  onCopy: (value: string) => void;
}

const CommandBlock = ({ title, command, copyLabel, onCopy }: CommandBlockProps) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <p className="font-mulish text-[14px] font-bold text-[#001943]">{title}</p>
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        border: "1px solid #E5E5E5",
        borderRadius: "4px",
        p: 1.5,
        backgroundColor: "#F8F8F8",
      }}
    >
      <Box
        component="pre"
        sx={{
          flex: 1,
          m: 0,
          fontFamily: "monospace",
          fontSize: 13,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {command}
      </Box>
      <Tooltip title="Copy to clipboard" arrow>
        <IconButton aria-label={copyLabel} onClick={() => onCopy(command)} size="small">
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
);

interface ProvisionTokenSuccessViewProps {
  provisionResponse: EdgeDeviceProvisionTokenResponse;
  isDownloadingBootstrap: boolean;
  bootstrapDownloadError: string;
  onDownloadBootstrapZip: () => void;
  showBootstrapDownload?: boolean;
  commandOptions?: EdgeDeviceProvisionCommandOptions;
}

export const ProvisionTokenSuccessView = ({
  provisionResponse,
  isDownloadingBootstrap,
  bootstrapDownloadError,
  onDownloadBootstrapZip,
  showBootstrapDownload = true,
  commandOptions,
}: ProvisionTokenSuccessViewProps) => {
  const [copiedOpen, setCopiedOpen] = React.useState(false);
  const [copiedMessage, setCopiedMessage] = React.useState("Token copied to clipboard");

  const handleCopyCommand = async (value: string) => {
    try {
      await copyTextToClipboard(value);
      setCopiedMessage("Command copied to clipboard");
      setCopiedOpen(true);
    } catch {
      // no-op, user can retry
    }
  };

  const formattedExpiresAt = provisionResponse.expires_at
    ? dateConvertor(provisionResponse.expires_at)
    : "";

  const handleCopyToken = async () => {
    if (!provisionResponse.provision_token) {
      return;
    }

    try {
      await copyTextToClipboard(provisionResponse.provision_token);
      setCopiedMessage("Token copied to clipboard");
      setCopiedOpen(true);
    } catch {
      // no-op, user can retry
    }
  };

  return (
    <>
      <p className="font-mulish text-[16px] text-[#001943]">
        Here is your Provision Token, which will be active until {formattedExpiresAt}:
      </p>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          border: "1px solid #E5E5E5",
          borderRadius: "4px",
          p: 1.5,
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            fontFamily: "monospace",
            fontSize: 14,
            overflowWrap: "anywhere",
            wordBreak: "break-all",
          }}
        >
          {provisionResponse.provision_token}
        </Box>
        <Tooltip title="Copy to clipboard" arrow>
          <IconButton
            aria-label="copy provision token to clipboard"
            onClick={handleCopyToken}
            size="small"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {showBootstrapDownload && (
        <>
          <OrangeTextButton
            onClick={onDownloadBootstrapZip}
            text={
              isDownloadingBootstrap ? "Downloading..." : "Download Bootstrap ZIP"
            }
            disabled={isDownloadingBootstrap}
          />
          {bootstrapDownloadError && (
            <FormHelperText className="font-mulish text-red-600" role="alert">
              {bootstrapDownloadError}
            </FormHelperText>
          )}
        </>
      )}
      {commandOptions && (
        <>
          <p className="font-mulish text-[16px] text-[#001943]">
            Run one of these commands from the unzipped bootstrap scripts:
          </p>
          <CommandBlock
            title="Provision (Linux / macOS)"
            command={buildBashProvisionCommand(
              provisionResponse.provision_token,
              commandOptions
            )}
            copyLabel="copy Linux / macOS provision command to clipboard"
            onCopy={handleCopyCommand}
          />
          <CommandBlock
            title="Provision (Windows PowerShell)"
            command={buildPowershellProvisionCommand(
              provisionResponse.provision_token,
              commandOptions
            )}
            copyLabel="copy Windows PowerShell provision command to clipboard"
            onCopy={handleCopyCommand}
          />
        </>
      )}

      <Snackbar
        open={copiedOpen}
        autoHideDuration={4000}
        onClose={() => setCopiedOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity="success"
          onClose={() => setCopiedOpen(false)}
          sx={{ width: "100%", fontFamily: "Mulish" }}
        >
          {copiedMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

interface Props {
  serialNumber: string;
  macAddress: string;
}

export const EdgeDeviceProvisioningSection = ({
  serialNumber,
  macAddress,
}: Props) => {
  const [provisionResponse, setProvisionResponse] =
    React.useState<EdgeDeviceProvisionTokenResponse | null>(null);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isDownloadingBootstrap, setIsDownloadingBootstrap] = React.useState(false);
  const [bootstrapDownloadError, setBootstrapDownloadError] = React.useState("");

  const trimmedSerial = serialNumber.trim();
  const trimmedMac = macAddress.trim();
  const canGenerate = Boolean(trimmedSerial || trimmedMac);

  React.useEffect(() => {
    setProvisionResponse(null);
    setErrorMessage("");
    setIsGenerating(false);
    setBootstrapDownloadError("");
  }, [serialNumber, macAddress]);

  const handleGenerateToken = async () => {
    setErrorMessage("");
    setIsGenerating(true);

    try {
      const response = await requestEdgeDeviceProvisionToken({
        serial_number: trimmedSerial,
        mac_address: trimmedMac,
      });
      setProvisionResponse(response);
    } catch (reason) {
      setErrorMessage(parseProvisionTokenError(reason));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadBootstrapZip = async () => {
    setBootstrapDownloadError("");
    setIsDownloadingBootstrap(true);

    try {
      await downloadEdgeDeviceBootstrapZip();
    } catch {
      setBootstrapDownloadError("Failed to download bootstrap ZIP.");
    } finally {
      setIsDownloadingBootstrap(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {isGenerating ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 2,
          }}
        >
          <CircularProgress size={32} />
          <Typography sx={{ fontFamily: "Mulish", fontSize: 16, color: "#001943" }}>
            Getting Edge Device token...
          </Typography>
        </Box>
      ) : provisionResponse ? (
        <ProvisionTokenSuccessView
          provisionResponse={provisionResponse}
          isDownloadingBootstrap={isDownloadingBootstrap}
          bootstrapDownloadError={bootstrapDownloadError}
          onDownloadBootstrapZip={handleDownloadBootstrapZip}
          showBootstrapDownload={false}
        />
      ) : (
        <>
          <OrangeTextButton
            onClick={handleGenerateToken}
            text="Generate Provisional Token (15 mins)"
            disabled={!canGenerate}
          />
          {errorMessage && (
            <FormHelperText className="font-mulish text-red-600" role="alert">
              {errorMessage}
            </FormHelperText>
          )}
        </>
      )}
      <OrangeTextButton
        onClick={handleDownloadBootstrapZip}
        text={isDownloadingBootstrap ? "Downloading..." : "Download Bootstrap ZIP"}
        disabled={isDownloadingBootstrap}
      />
      {bootstrapDownloadError && (
        <FormHelperText className="font-mulish text-red-600" role="alert">
          {bootstrapDownloadError}
        </FormHelperText>
      )}
    </Box>
  );
};
